import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { mdiClose } from '@mdi/js';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { FieldValues, useFieldArray, useFormContext } from 'react-hook-form';

import { IOfferEventDateInput, IContentTypeValue } from 'graphql/types';
import { getRoundedDateTime } from 'common/utils/formatDate';
import { IconButton } from 'common/components/IconButton';
import { Divider } from 'common/components/Divider';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';
import { AddButton } from 'common/components/Button/AddButton';
import { DateTimePickerField } from 'common/forms/Field';
import { FormTrainerSelectorField } from 'user/forms/Field';

interface IProps {
    extraFields?(
        dateFieldName: string,
        index: number,
        // Date passed to extraFields with a parsed startDate and endDate
        date: Omit<IOfferEventDateInput, 'startDate' | 'endDate'> & {
            startDate?: DateTime;
            endDate?: DateTime;
        }
    ): React.ReactNode;
    extraFieldsInitialValues?: {
        location?: string | null;
        isServiceProviderWebinar?: boolean;
        webinarLink?: string | null;
    };
    name: string;
    offerEventId?: string;
}

export const MultipleDateRangeFields = <TFieldValues extends FieldValues>({
    control: formControl,
    extraFields,
    extraFieldsInitialValues,
    name,
    offerEventId,
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();

    const control = asTypedControl<IOfferEventDateInput[]>(name, formControl);

    const { setValue } = useFormContext();

    const {
        fields: dates,
        append,
        remove,
    } = useFieldArray({
        control,
        name,
        keyName: 'key',
    });

    // Using setValue instead of useFieldArray.update keeps the field-array
    // key stable, so the DateTimePicker doesn't remount and lose focus while
    // the user is typing into a date section.
    const handleFieldChange = (
        dates: IOfferEventDateInput[],
        index: number,
        newStartDate: DateTime
    ) => {
        setValue(`${name}[${index}].startDate`, newStartDate, {
            shouldDirty: true,
        });

        if (newStartDate?.isValid) {
            const currentEndDate = dates[index].endDate;

            // When start is equal or higher as end, we change end to be start + 1 hour
            if (newStartDate >= currentEndDate) {
                setValue(
                    `${name}[${index}].endDate`,
                    newStartDate.plus({ hours: 1 }),
                    { shouldDirty: true }
                );
            }
        }
    };

    const handleAddDate = () => {
        append({
            startDate: getRoundedDateTime(),
            endDate: getRoundedDateTime().plus({
                hours: 1,
            }),
            ...extraFieldsInitialValues,
        });
    };

    return (
        <Stack divider={<Divider />} spacing={2} sx={{ mb: 2 }}>
            {dates?.map((dateField, index) => {
                const { startDate, endDate } = dateField;
                const minEndDate = startDate
                    ? startDate.plus({ hours: 1 })
                    : undefined;

                const hasMultipleDates = dates.length > 1;

                return (
                    <Box key={`dateRow-${index}`}>
                        <Grid container sx={{ alignItems: 'center' }}>
                            <Grid size={{ sm: 'grow' }}>
                                <Typography
                                    sx={{ fontWeight: 'bold' }}
                                    variant="h3"
                                >
                                    {translate('dateNumber', {
                                        number: index + 1,
                                    })}
                                </Typography>
                            </Grid>

                            {hasMultipleDates && (
                                <Grid>
                                    <IconButton
                                        iconPath={mdiClose}
                                        iconSize="2.4rem"
                                        size="large"
                                        onClick={() => {
                                            remove(index);
                                        }}
                                    />
                                </Grid>
                            )}
                        </Grid>

                        <Grid container>
                            <Grid
                                size={{
                                    sm: 'grow',
                                    xs: hasMultipleDates ? 5 : 6,
                                }}
                            >
                                <DateTimePickerField
                                    control={control}
                                    key={`${dateField.key}-start`}
                                    label={translate('startDate')}
                                    name={`${name}[${index}].startDate`}
                                    onChange={(newStartDate: DateTime) =>
                                        handleFieldChange(
                                            dates,
                                            index,
                                            newStartDate
                                        )
                                    }
                                />
                            </Grid>

                            <Grid
                                size={{
                                    sm: 'grow',
                                    xs: hasMultipleDates ? 5 : 6,
                                }}
                                sx={{ pl: 2 }}
                            >
                                <DateTimePickerField
                                    control={control}
                                    key={`${dateField.key}-end`}
                                    label={translate('endDate')}
                                    minDate={minEndDate}
                                    name={`${name}[${index}].endDate`}
                                />
                            </Grid>
                        </Grid>

                        {!!extraFields &&
                            extraFields(`dates[${index}]`, index, {
                                ...dates[index],
                                startDate: startDate?.isValid
                                    ? startDate
                                    : undefined,
                                endDate: endDate?.isValid ? endDate : undefined,
                            })}

                        <Box sx={{ mt: 2 }}>
                            <FormTrainerSelectorField
                                control={control}
                                label={translate('addTrainer')}
                                name={`dates[${index}].trainers`}
                                selectorQueryVariables={
                                    !offerEventId
                                        ? undefined
                                        : {
                                              contentType:
                                                  IContentTypeValue.OfferEvent,
                                              objectId: offerEventId,
                                          }
                                }
                            />
                        </Box>
                    </Box>
                );
            })}

            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                }}
            >
                <AddButton
                    aria-label={translate('addDate')}
                    onClick={handleAddDate}
                >
                    {translate('addDate')}
                </AddButton>
            </Box>
        </Stack>
    );
};
